import { noop, pull } from 'lodash';
import { reactive } from 'vue';
import DossierClient from '@/dossier/client/DossierClient';
import Dossier from '@/dossier/models/Dossier';
import Identity from '@/general/models/Identity';
import { WorkshopTeamId } from '@/general/models/WorkshopTeam';
import AuthService from '@/general/services/AuthService';
import pushService, { Unbinder } from '@/general/services/PushService';
import Permission from '@/shared/constants/Permission';
import { parseNumericId } from '@/shared/converters/commonConverters';
import { parseMoment } from '@/shared/converters/dateConverters';
import { parseUser } from '@/shared/converters/userConverters';
import AssignmentData from '@/shared/models/AssignmentData';
import { BrandId } from '@/shared/models/Brand';
import { OutletId } from '@/shared/models/Outlet';
import serviceEvents, { UserLoggedIn, UserLoggedOut } from '@/shared/services/events/serviceEvents';
import { extractIds, removeById } from '@/shared/utils';
import { isPresent, present } from '@/shared/utils/typescript';
import DossierStackClient from '../client/DossierStackClient';
import MemoClient from '../client/MemoClient';
import TaskClient from '../client/TaskClient';
import { parseCleaningTask } from '../converters/cleaningTaskConverters';
import { parseTask } from '../converters/taskConverters';
import { DossierStackId } from '../models/DossierStack';
import { DossierStackItemId } from '../models/DossierStackItem';
import { DossierTaskContext } from '../models/DossierTaskContext';
import InvoicingTask from '../models/InvoicingTask';
import Presence from '../models/Presence';
import Task, { TaskId } from '../models/Task';
import TaskContext from '../models/TaskContext';
import TaskOverviewContext from '../models/TaskOverviewContext';

class TaskService {
  private globalUpdateUnbinder: Unbinder = noop;
  private updatedTaskIds: Array<TaskId> = [];
  private ignoredTaskIds: Array<TaskId> = [];

  startEventListeners(): this {
    serviceEvents.on(UserLoggedIn, e => this.bindGlobalUpdateHandler(e.user));
    serviceEvents.on(UserLoggedOut, () => {
      this.globalUpdateUnbinder();
      this.globalUpdateUnbinder = noop;
    });
    return this;
  }

  hasAnyUpdates(): boolean {
    return this.updatedTaskIds.length > 0;
  }

  hasUpdatesForTask(taskId: TaskId): boolean {
    return this.updatedTaskIds.includes(taskId);
  }

  clearGlobalUpdates(): void {
    this.updatedTaskIds = [];
  }

  async loadInvoicingSummary(context: TaskOverviewContext): Promise<void> {
    const user = AuthService.getCurrentUser();
    if (user.hasAllPermissions(Permission.ACCESS_TO_DOSSIERS, Permission.CAN_CHECKOUT)) {
      context.invoicingSummary = await TaskClient.loadInvoicingSummary(context.filters.asTaskCriteria());
    } else {
      context.invoicingSummary = null;
    }
  }

  async loadDossier(dossier: Dossier): Promise<DossierTaskContext> {
    const tasks = await TaskClient.loadByDossier(dossier.id);
    const memos = await MemoClient.loadAllForDossier(dossier.id);
    const context = reactive(new DossierTaskContext(dossier, tasks, memos)) as DossierTaskContext;
    this.bindDossierUpdateHandlers(context);
    this.ignoredTaskIds = extractIds(tasks);
    context.freeHooks.push(() => this.ignoredTaskIds = []);
    return context;
  }

  async assign(task: Task, target: AssignmentData, context: TaskContext): Promise<void> {
    const groupId = target && 'groupId' in target ? target.groupId : null;
    const userId = target && 'userId' in target ? target.userId : null;
    const currentUser = AuthService.getCurrentUser();
    if (groupId !== null) {
      await TaskClient.assignGroup(task.id, groupId);
      task.isGroupTask = true;
      if (!currentUser.isMemberOfGroup(groupId)) context.removeTask(task.id);
    } else if (userId === null) {
      await TaskClient.releaseOwnership(task.id);
      task.ownedBy = null;
    } else if (userId === currentUser.id) {
      await TaskClient.takeOwnership(task.id);
      task.ownedBy = currentUser;
    } else {
      await TaskClient.assignUser(task.id, userId);
      context.removeTask(task.id);
    }
  }

  async markAsInvoiced(task: InvoicingTask, context: TaskOverviewContext): Promise<void> {
    const dossierId = present(task.dossier).id;
    await DossierClient.updateInvoicedStatus(dossierId, true);
    context.removeInvoicingTask(dossierId);
  }

  async markAsResolved(taskId: TaskId, context: TaskContext): Promise<void> {
    await TaskClient.markAsResolved(taskId);
    context.resolveTask(taskId, AuthService.getCurrentUser());
  }

  async togglePresence(presence: Presence, pin: string): Promise<void> {
    await TaskClient.updatePresence(!presence.isPresent, pin, presence.outletId);
    presence.toggle();
  }

  async handleKeyDrop(taskId: TaskId, email: string | null, mobileNumber: string | null, context: TaskOverviewContext): Promise<void> {
    await TaskClient.handleKeyDrop(taskId, email, mobileNumber);
    context.removeTask(taskId);
  }

  private bindGlobalUpdateHandler(user: Identity): void {
    this.globalUpdateUnbinder();
    this.globalUpdateUnbinder = pushService.bind(`event-tasks-${user.id}/event-task-created`, data => {
      const task = present(parseTask(data));
      if (!this.ignoredTaskIds.includes(task.id)) this.updatedTaskIds.push(task.id);
    });
  }

  bindOverviewUpdateHandlers(context: TaskOverviewContext): void {
    const user = AuthService.getCurrentUser();
    const userChannel = `event-tasks-${(user.id)}`;

    context.freeHooks.push(pushService.bind(`${userChannel}/reload-assigneddossiers`, async () => {
      context.planningTasks = await TaskClient.loadPlanningTasks(context.filters.asTaskCriteria());
    }));

    if (!isPresent(user.dealerGroupId)) return;
    const groupChannel = `event-tasks-group-${(user.dealerGroupId)}`;

    context.freeHooks.push(pushService.bind(`event-tasks-${user.id}/event-task-created`, data => {
      const outletId = present(parseNumericId<OutletId>(data.moboOutletId), 'missing outletId');
      const brandId = parseNumericId<BrandId>(data.brandId);
      const teamId = parseNumericId<WorkshopTeamId>(data.teamId);
      const task = present(parseTask(data));
      if (context.filters.matches(outletId, brandId, teamId, task.status)) context.addTask(task);
    }));

    context.freeHooks.push(pushService.bind(`${groupChannel}/event-task-updated`, data => {
      context.updateTaskOwner(parseUser(data.ownedBy), data.taskId);
    }));

    context.freeHooks.push(pushService.bind(`${groupChannel}/event-task-resolved`, data => {
      context.resolveTask(data.taskId);
    }));

    context.freeHooks.push(pushService.bind(`${groupChannel}/dossiers-invoiced-resolved`, data => {
      context.removeInvoicingTask(data.dossierId);
    }));

    context.freeHooks.push(pushService.bind(`${groupChannel}/dossier-stack-event`, async data => {
      const stackId = present(parseNumericId<DossierStackId>(data.stackId), 'missing stackId');
      const stackItemId = parseNumericId<DossierStackItemId>(data.contentId);
      switch (data.event) {
        case 'CREATE': {
          context.updateStackSize(stackId, data.countOfDossiers);
          if (context.currentStack?.id === stackId) {
            context.currentStackItems = await DossierStackClient.loadItems(stackId, context.filters.common.activeOutletIds);
          }
          break;
        }
        case 'UPDATE': {
          if (context.currentStack?.id === stackId && isPresent(stackItemId)) {
            const item = context.currentStackItems.find(i => i.id === stackItemId);
            item?.update({
              description: data.memo || '',
              deadline: parseMoment(data.deadline),
            });
          }
          break;
        }
        case 'DELETE': {
          context.updateStackSize(stackId, data.countOfDossiers);
          if (context.currentStack?.id === stackId && isPresent(stackItemId)) removeById(context.currentStackItems, stackItemId);
          break;
        }
      }
    }));
  }

  bindCleaningOverviewUpdateHandlers(context: TaskOverviewContext): void {
    const user = AuthService.getCurrentUser();
    if (!isPresent(user.dealerGroupId)) return;
    const cleaningChannel = `dossiercleaning-${user.dealerGroupId}`;

    context.freeHooks.push(pushService.bind(`${cleaningChannel}/dossier-update`, async data => {
      const newTask = present(parseCleaningTask(data));
      const existingTask = context.cleaningTasks.find(ct => ct.dossier?.id === newTask.dossier?.id);
      if (context.filters.matchesCleaningTask(newTask)) {
        if (existingTask) {
          existingTask.updateWith(newTask);
        } else {
          context.cleaningTasks = await TaskClient.loadCleaningTasks(context.filters.asCleaningCriteria());
        }
      } else if (existingTask) {
        pull(context.cleaningTasks, existingTask);
      }
    }));

    context.freeHooks.push(pushService.bind(`${cleaningChannel}/dossier-checkin`, async data => {
      const outletId = present(parseNumericId<OutletId>(data.outletId), 'missing outletId');
      const brandId = parseNumericId<BrandId>(data.brandId);
      const teamId = parseNumericId<WorkshopTeamId>(data.teamId);
      if (context.filters.matches(outletId, brandId, teamId)) {
        context.cleaningTasks = await TaskClient.loadCleaningTasks(context.filters.asCleaningCriteria());
      }
    }));
  }

  private bindDossierUpdateHandlers(context: DossierTaskContext): void {
    const user = AuthService.getCurrentUser();
    if (!isPresent(user.dealerGroupId)) return;

    const groupChannel = `event-tasks-group-${(user.dealerGroupId)}`;

    context.freeHooks.push(pushService.bind(`event-tasks-${user.id}/event-task-created`, data => {
      const task = present(parseTask(data));
      if (task.dossier?.id === context.dossier.id) {
        context.addTask(task);
        this.ignoredTaskIds.push(task.id);
      }
    }));

    context.freeHooks.push(pushService.bind(`${groupChannel}/event-task-resolved`, data => {
      context.resolveTask(data.taskId);
    }));
  }
}

export default reactive(new TaskService()).startEventListeners();